import { useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { type SummaryTopic } from '../../__generated__/graphql/catalog/graphql'
import { shortenShopifyId } from '../../app/_utils/shortenShopifyId'
import { FETCH_FILTERED_PRODUCT_REVIEWS, FETCH_PRODUCT_REVIEWS } from '../../lib/graphql/queries'
import { QueryStatus } from '../../lib/hooks/types'
import { ProductReviewSet } from '../../types/graphql'
import { fieldsByFieldType } from '../reviews/constants'
import { ProductTag } from '../reviews/types'
import { decodeHtmlEntities, getProductTagFromString } from '../reviews/utils'

export type UseProductReviewsProps = {
  shopifyProductId: string | null | undefined
  filters?: ProductReviewsFilters // NOTE: ensure this inbound `filters` value is always memoized
  pageNum?: number
  skip?: boolean
}

export type UseProductReviewsOutput = {
  productReviews: MergedProductReviewSet | undefined
  productReviewsStatus: QueryStatus
  productReviewsError: Error | undefined
}

export type MergedProductReviewSet = {
  totalFilteredReviews: number | null
  reviewSummary?: Array<SummaryTopic> | null
} & ProductReviewSet

type FilterCustomField = {
  questionId: number
  answers: string[]
}

export type ProductReviewsFilters = {
  search?: string
  rating?: string[]
  size?: string[]
  length?: string[]
  weight?: string[]
  height?: string[]
  fit?: string[]
  occupation?: string[]
  mediaType?: string[]
}

export const useProductReviews = ({
  shopifyProductId: unsanitizedShopifyProductId,
  filters,
  pageNum = 1,
  skip,
}: UseProductReviewsProps): UseProductReviewsOutput => {
  const shopifyProductId = shortenShopifyId(unsanitizedShopifyProductId ?? '')

  const {
    data: fetchProductReviewsData,
    loading: fetchProductReviewsLoading,
    error: fetchProductReviewsError,
    called: fetchProductReviewsCalled,
    previousData: fetchProductReviewsPreviousData,
  } = useQuery(FETCH_PRODUCT_REVIEWS, {
    variables: { shopifyProductId: shopifyProductId, numPerPage: 1, pageNum: 1 },
    skip: skip || shopifyProductId.length === 0,
  })

  const customReviewFields = useMemo(() => {
    const out: FilterCustomField[] = []
    if (filters && fetchProductReviewsData?.reviews?.tag) {
      const productTag = fetchProductReviewsData.reviews.tag as ProductTag
      if (filters.size && fieldsByFieldType[productTag]?.size) {
        const questionId = fieldsByFieldType[productTag].size!.fieldId
        out.push({ questionId: questionId, answers: filters.size })
      }
      if (filters.length && fieldsByFieldType[productTag]?.length) {
        const questionId = fieldsByFieldType[productTag].length!.fieldId
        out.push({ questionId: questionId, answers: filters.length })
      }
      if (filters.weight && fieldsByFieldType[productTag]?.weight) {
        const { fieldId, values } = fieldsByFieldType[productTag].weight!
        const answers: string[] = []
        for (const weightFilter of filters.weight) {
          const weightRangeParts = weightFilter.split('-')
          const matchingVal = values.find(
            ({ min, max }) => min == weightRangeParts[0] && max == weightRangeParts[1]
          )
          if (matchingVal) {
            answers.push(...(matchingVal.value as string[]))
          }
        }
        out.push({ questionId: fieldId, answers })
      }
      if (filters.height && fieldsByFieldType[productTag]?.height) {
        const questionId = fieldsByFieldType[productTag].height!.fieldId
        out.push({ questionId: questionId, answers: filters.height })
      }
      if (filters.fit && fieldsByFieldType[productTag]?.fit) {
        const questionId = fieldsByFieldType[productTag].fit!.fieldId
        out.push({ questionId: questionId, answers: filters.fit })
      }
      if (filters.occupation && fieldsByFieldType[productTag]?.occupation) {
        const questionId = fieldsByFieldType[productTag].occupation!.fieldId
        out.push({ questionId: questionId, answers: filters.occupation })
      }
    }
    return out
  }, [fetchProductReviewsData?.reviews?.tag, filters])

  const {
    data: fetchFilteredProductReviewsData,
    loading: fetchFilteredProductReviewsLoading,
    error: fetchFilteredProductReviewError,
    called: fetchFilteredProductReviewsCalled,
    previousData: fetchFilteredProductReviewsPreviousData,
  } = useQuery(FETCH_FILTERED_PRODUCT_REVIEWS, {
    variables: {
      filterInput: {
        productId: shopifyProductId,
        freeTextSearch: filters?.search,
        scores: filters?.rating?.map(e => Number(e)),
        crfs: customReviewFields,
        // Regardless of value, if filters are present we want `pictured: true`
        pictured: (filters?.mediaType?.length ?? 0) > 0 ? true : undefined,
      },
      numPerPage: 10,
      pageNum,
    },
    skip: skip || shopifyProductId.length === 0,
  })

  // TODO: manual processing when `mediaType` filter has values
  // and BE can provide image vs video metadata
  const productReviews = useMemo((): MergedProductReviewSet | undefined => {
    const reviewsResponse =
      fetchProductReviewsData?.reviews ?? fetchProductReviewsPreviousData?.reviews
    const filterReviewsResponse =
      fetchFilteredProductReviewsData?.filteredReviews ??
      fetchFilteredProductReviewsPreviousData?.filteredReviews

    if (!(reviewsResponse && filterReviewsResponse)) return undefined

    const productTag = getProductTagFromString(filterReviewsResponse.tag)

    return {
      averageFit: reviewsResponse.averageFit,
      averageScore: reviewsResponse.averageScore,
      totalReviews: reviewsResponse.totalReviews,
      totalFilteredReviews: filterReviewsResponse.pagination
        ? filterReviewsResponse.pagination.numPages
        : null,
      starDistribution: reviewsResponse.starDistribution,
      tag: productTag ? productTag : null,
      pagination: filterReviewsResponse.pagination
        ? {
            ...filterReviewsResponse.pagination,
            // NOTE: we have to recalculate this value because the `numPages` field on `filterReviewsResponse`
            // is used to store the count of filtered reviews, not the number of pages. This is a
            // limitation of Yotpo responses what we must use in our Java reviews service.
            numPages: Math.ceil(
              (filterReviewsResponse.pagination.numPages ?? 0) /
                (filterReviewsResponse.pagination.numPerPage ?? 10)
            ),
          }
        : null,
      reviews: filterReviewsResponse.reviews
        ? filterReviewsResponse!
            .reviews!.filter(review => review !== null)
            .map(review => {
              return {
                reviewer: review!.reviewer,
                createdAt: review!.createdAt ? new Date(review!.createdAt) : null,
                title: decodeHtmlEntities(review!.title),
                content: decodeHtmlEntities(review!.content),
                imageUrls: review!.imageUrls
                  ? review!.imageUrls.filter(url => url !== null).map(url => url!)
                  : null,
                score: review!.score,
                customFields: review!.customFields
                  ? review!.customFields
                      .filter(e => e !== null)
                      .map(field => ({
                        // `48919` is the default 'fit' fieldId for when products aren't tagged in Shopify.
                        // This will ensure that we treat all default 'fit' fields as our tagged 'fit' field.
                        key: field!.key === 48919 ? 48922 : field!.key,
                        title: field!.title,
                        formId: field!.formId,
                        value: field!.value,
                      }))
                  : [],
              }
            })
        : [],
      reviewSummary: fetchProductReviewsData?.reviewSummary ?? null,
    }
  }, [
    fetchFilteredProductReviewsData?.filteredReviews,
    fetchFilteredProductReviewsPreviousData?.filteredReviews,
    fetchProductReviewsData?.reviews,
    fetchProductReviewsPreviousData?.reviews,
    fetchProductReviewsData?.reviewSummary,
  ])

  const productReviewsStatus = useMemo<QueryStatus>(() => {
    if (!(fetchProductReviewsCalled || fetchFilteredProductReviewsCalled)) return 'idle'
    if (fetchProductReviewsLoading || fetchFilteredProductReviewsLoading) return 'pending'
    if (fetchProductReviewsError || fetchFilteredProductReviewError) return 'rejected'
    return 'resolved'
  }, [
    fetchFilteredProductReviewError,
    fetchFilteredProductReviewsCalled,
    fetchFilteredProductReviewsLoading,
    fetchProductReviewsCalled,
    fetchProductReviewsError,
    fetchProductReviewsLoading,
  ])

  return {
    productReviews,
    productReviewsStatus,
    productReviewsError: fetchProductReviewsError ?? fetchFilteredProductReviewError,
  }
}
